import { Thread } from '@/components/assistant-ui/thread';
import { ThreadList } from '@/components/assistant-ui/thread-list';
import { ChapterSidebar } from '@/components/layout/sidebars/chapter-sidebar';
import { Button } from '@/components/ui/button';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { cn } from '@/lib/utils';
import { ProjectChatGuard } from '@/pages/ai-chat/remote-threadlist-adapter';
import { ChapterEntitiesTab } from '@/pages/chapters/components/chapter-entities-tab';
import { ChapterOutlineTab } from '@/pages/chapters/components/chapter-outline-tab';
import { ChapterPlotcardsTab } from '@/pages/chapters/components/chapter-plotcards-tab';
import { useAssistantApi } from '@assistant-ui/react';
import { ArrowLeft, BookOpen, FileText, History, Layers, MessageCircle, PlusIcon, Clapperboard } from 'lucide-react';
import { ChapterSceneBuilderTab } from '@/pages/chapters/components/chapter-scene-builder-tab';
import { useLicense } from '@/hooks/useLicense';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { type ImperativePanelHandle } from 'react-resizable-panels';
import { useParams } from 'react-router-dom';

type PanelType = 'navigation' | 'outline' | 'scenes' | 'plot' | 'entities' | 'chat';

function ChatSidebarContent() {
    const [view, setView] = useState<'chat' | 'list'>('chat');
    const api = useAssistantApi();

    const handleNewChat = () => {
        api.threads().switchToNewThread();
        setView('chat');
    };

    return (
        <div className="flex flex-col h-full w-full bg-background">
            <div className="px-4 py-3 border-b flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                    {view === 'list' && (
                        <Button variant="ghost" size="icon" onClick={() => setView('chat')} className="-ml-2 h-8 w-8">
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                    )}
                    <span className="font-semibold">{view === 'list' ? 'Threads' : 'AI Assistant'}</span>
                </div>
                <div className="flex items-center gap-1">
                    {view === 'chat' && (
                        <Button variant="ghost" size="sm" onClick={() => setView('list')} className="h-8 text-muted-foreground">
                            <History className="h-4 w-4 mr-2" />
                            History
                        </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={handleNewChat} title="New Chat" className="h-8 w-8">
                        <PlusIcon className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            <div className="flex-1 overflow-hidden relative flex flex-col">
                {view === 'list' ? (
                    <div className="p-4 overflow-y-auto scrollbar-hide flex-1">
                        <ThreadList onSelect={() => setView('chat')} />
                    </div>
                ) : (
                    <div className="flex-1 h-full min-h-0">
                        <Thread viewportClassName="px-4" />
                    </div>
                )}
            </div>
        </div>
    );
}


export function RightSidebarSwitcher({ children }: { children: React.ReactNode }) {
    // Self-contained open/close state — no SidebarProvider needed
    const [open, setOpen] = useState(true);
    const [activePanel, setActivePanel] = useState<PanelType>('navigation');
    const { chapterId } = useParams();
    const { hasAIFeatures } = useLicense();
    const rightPanelRef = useRef<ImperativePanelHandle>(null);

    // Sync open state → panel collapse/expand
    useEffect(() => {
        const panel = rightPanelRef.current;
        if (!panel) return;

        if (open) {
            if (panel.getSize() === 0) {
                panel.expand();
            }
        } else {
            if (panel.getSize() > 0) {
                panel.collapse();
            }
        }
    }, [open]);

    const togglePanel = (panel: PanelType) => {
        if (activePanel === panel && open) {
            setOpen(false);
        } else {
            setActivePanel(panel);
            setOpen(true);
        }
    };

    // Sync panel collapse/expand → open state (e.g. user drags handle all the way)
    const handlePanelCollapse = useCallback(() => {
        setOpen(false);
    }, []);

    const handlePanelExpand = useCallback(() => {
        setOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const renderActivePanel = () => {
        switch (activePanel) {
            case 'navigation':
                return <ChapterSidebar onClose={handleClose} />;
            case 'outline':
                return chapterId ? <ChapterOutlineTab chapterId={chapterId} /> : <div className="p-4 text-muted-foreground text-sm">Select a chapter to view its outline.</div>;
            case 'scenes':
                return chapterId ? <ChapterSceneBuilderTab chapterId={chapterId} /> : <div className="p-4 text-muted-foreground text-sm">Select a chapter to use the Scene Builder.</div>;
            case 'plot':
                return chapterId ? <ChapterPlotcardsTab chapterId={chapterId} /> : <div className="p-4 text-muted-foreground text-sm">Select a chapter to view its plot cards.</div>;
            case 'entities':
                return chapterId ? <ChapterEntitiesTab chapterId={chapterId} /> : <div className="p-4 text-muted-foreground text-sm">Select a chapter to view its entities.</div>;
            case 'chat':
                return (
                    <ProjectChatGuard>
                        <ChatSidebarContent />
                    </ProjectChatGuard>
                );
            default:
                return null;
        }
    };

    return (
        <div className="flex h-full w-full bg-transparent overflow-hidden relative">
            <ResizablePanelGroup
                direction="horizontal"
                className="flex-1"
                autoSaveId="chapter-right-sidebar"
            >
                {/* Main Content Area */}
                <ResizablePanel
                    id="main-content"
                    defaultSize={75}
                    minSize={30}
                    order={1}
                    className="flex min-w-0"
                >
                    {children}
                </ResizablePanel>

                {/* Resize Handle — always in DOM, disabled when collapsed */}
                <ResizableHandle
                    disabled={!open}
                    className={cn(
                        "w-1 relative z-50 transition-colors bg-transparent border-0",
                        open
                            ? "cursor-col-resize hover:bg-primary/10 active:bg-primary/20"
                            : "w-0 opacity-0"
                    )}
                    withHandle={true}
                />

                {/* Right Sidebar Panel — always in DOM, collapses to 0 */}
                <ResizablePanel
                    ref={rightPanelRef}
                    id="right-sidebar"
                    order={2}
                    collapsible
                    collapsedSize={0}
                    defaultSize={25}
                    minSize={15}
                    maxSize={40}
                    onCollapse={handlePanelCollapse}
                    onExpand={handlePanelExpand}
                    className="flex min-w-0"
                >
                    {/* Only render content when expanded to save resources */}
                    {open && (
                        <div className="flex-1 flex flex-col relative w-full h-[calc(100%-1rem)] pr-2 pb-2 mt-4 min-w-0">
                            <div className="flex-1 w-full overflow-hidden flex flex-col relative bg-background rounded-[24px] shadow-sm border min-h-0 backdrop-blur-md">
                                <div className="h-full w-full overflow-y-auto scrollbar-hide">
                                    {renderActivePanel()}
                                </div>
                            </div>
                        </div>
                    )}
                </ResizablePanel>
            </ResizablePanelGroup>

            {/* Vertical Icon Switcher Strip */}
            <div className="flex flex-col relative w-auto h-[calc(100%-1rem)] pr-2 pb-2 mt-4 pl-2 shrink-0">
                <div className="h-full w-12 border border-border bg-card rounded-[24px] shadow-sm flex flex-col items-center py-3 gap-3 z-10 overflow-hidden">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => togglePanel('navigation')}
                        className={cn("h-10 w-10 rounded-xl transition-all", activePanel === 'navigation' && open ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted")}
                        title="Manuscript Navigation"
                    >
                        <BookOpen className="h-5 w-5" />
                    </Button>

                    <div className="w-6 h-px bg-border/50 my-1" />

                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => togglePanel('outline')}
                        className={cn("h-10 w-10 rounded-xl transition-all", activePanel === 'outline' && open ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted")}
                        title="Chapter Outline"
                    >
                        <FileText className="h-5 w-5" />
                    </Button>

                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => togglePanel('scenes')}
                        className={cn("h-10 w-10 rounded-xl transition-all", activePanel === 'scenes' && open ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted")}
                        title="Scene Builder"
                    >
                        <Clapperboard className="h-5 w-5" />
                    </Button>

                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => togglePanel('plot')}
                        className={cn("h-10 w-10 rounded-xl transition-all", activePanel === 'plot' && open ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted")}
                        title="Plot Cards"
                    >
                        <BookOpen className="h-5 w-5" />
                    </Button>

                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => togglePanel('entities')}
                        className={cn("h-10 w-10 rounded-xl transition-all", activePanel === 'entities' && open ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted")}
                        title="Entities"
                    >
                        <Layers className="h-5 w-5" />
                    </Button>

                    <div className="flex-1" />

                    {hasAIFeatures && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => togglePanel('chat')}
                            className={cn("h-10 w-10 rounded-xl transition-all", activePanel === 'chat' && open ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted")}
                            title="AI Chat"
                        >
                            <MessageCircle className="h-5 w-5" />
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
